import {Button} from '@dropbox/dig-components/buttons';
import {DatePickerInput} from '@dropbox/dig-components/date_picker';
import {FormHelperText, FormLabel, FormRow} from '@dropbox/dig-components/form_row';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {TextArea, TextInput} from '@dropbox/dig-components/text_fields';
import {Toggletip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Cluster, Split, Stack, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {InfoLine, LinkLine, LockLine, LockOpenLine} from '@dropbox/dig-icons/assets';
import {EndToEndStackMini, EndToEndWorkflowMini} from '@dropbox/dig-illustrations';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {snackbarAtom} from 'atoms/snackbar';
import {
  Employee,
  EmployeeLdap,
  EmployeeRoleLevel,
  TeamWithCounts,
  Workstream,
  WorkstreamCreate,
  WorkstreamDependencyEdit,
  WorkstreamDependencyType,
  WorkstreamEdit,
  WorkstreamEmployeeEdit,
  WorkstreamMetric,
  WorkstreamMetricVisibility,
  WorkstreamPrivacy,
} from 'client';
import {
  getPlainTextFromRichTextValue,
  isLexicalJsonString,
  toPlainTextLexicalJson,
} from 'components/DSYS/editor/utils/lexical';
import {LabelGroup} from 'components/DSYS/LabelGroup';
import {Layout} from 'components/DSYS/Layout';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {Title} from 'components/DSYS/Title';
import {isLinkValid, ProjectLinkEditor} from 'components/projects/ProjectLinkEditor';
import {EditSaveButtons} from 'components/shared/EditSaveButtons';
import {PeopleSearchMenu} from 'components/shared/PeopleSearchMenu';
import {ROUTE_PATHS} from 'constant';
import {format} from 'date-fns';
import {Link, sortEmployees} from 'helpers/utils';
import {getRequiredRoles, WORKSTREAM_ROLES, WorkstreamRoleId} from 'helpers/workstream_roles';
import {useConfirmUnsavedChanges} from 'hooks/useConfirmUnsavedChanges';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {ChangeEvent, Suspense, useEffect, useMemo, useState} from 'react';
import {Trans} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router';
import {toCalendarDate, toEODISOStringFromDate} from 'views/admin/utils';
import {useTimeframes} from 'views/workstreams/detail/hooks';

import {
  useAuditLogs,
  useCompanyWorkstreams,
  useMnpiAccess,
  usePrivacyText,
  useWorkstreamPlanningDocs,
} from './hooks';
import {
  getUniqueSelectedWorkstreamIds,
  ReordableWorkstreams,
  WorkstreamSortWrapper,
} from './ReorderableWorkstreams';
import {buildSidebarOriginState, isWorkstreamCompanyIndexPath} from './routeUtils';
import {
  getPendingParentTimeframe,
  getSubworkstreamTimeframeBlock,
  getSubworkstreamTimeframeRule,
} from './subworkstreamTimeframe';
import {getSoonestMetricDate, getUserType, isWorkstreamMetric} from './utils';
import {WorkstreamAuditLog} from './WorkstreamAuditLog';
import {MetricEditor} from './WorkstreamMetricEditor';
import {sortMetricsByCompleteDate} from './workstreamMetricSort';
import styles from './WorkstreamModify.module.css';
import {WorkstreamPrivacyDrawer} from './WorkstreamPrivacyDrawer';
import {WorkstreamSettingsDrawer} from './WorkstreamSettingsDrawer';

const WORKING_GROUP_ROLE_ID = 'working_group' as const;
type SingleWorkstreamRoleId = Exclude<WorkstreamRoleId, typeof WORKING_GROUP_ROLE_ID>;
type RoleAssignments = Partial<Record<SingleWorkstreamRoleId, EmployeeRoleLevel | undefined>>;
const WORKSTREAM_ROLE_ROWS: SingleWorkstreamRoleId[][] = [
  ['dri', 'reporter'],
  ['exec_sponsor', 'steam_owner'],
];

// This legacy editor cannot render identity-only redacted rows.
// `WorkstreamManageOutcomesModal` and `WorkstreamRedesignSections` provide the supported
// redacted-metric UI if this editor needs to support those rows in the future.
const isRedactedMetric = (metric: WorkstreamMetric) =>
  metric.visibility === WorkstreamMetricVisibility.REDACTED;

// Helper to initialize role assignments from workstream data
const initializeRoleAssignments = (ws?: Workstream): RoleAssignments => {
  const assignments: RoleAssignments = {};
  ws?.employee_associations?.forEach((assoc) => {
    if (assoc.dri in WORKSTREAM_ROLES && assoc.dri !== WORKING_GROUP_ROLE_ID) {
      assignments[assoc.dri as SingleWorkstreamRoleId] = assoc.employee;
    }
  });
  return assignments;
};

const initializeWorkingGroupAssignments = (ws?: Workstream): EmployeeRoleLevel[] =>
  ws?.employee_associations
    ?.filter((assoc) => assoc.dri === WORKING_GROUP_ROLE_ID)
    .map((assoc) => assoc.employee) ?? [];

const PrivacyInfo = ({
  workstream,
  metrics,
  owners,
  privateEmployees,
  privateTeams,
}: {
  workstream: WorkstreamEdit;
  metrics: WorkstreamMetric[];
  owners: WorkstreamEmployeeEdit[];
  privateEmployees?: EmployeeLdap[];
  privateTeams?: TeamWithCounts[];
}) => {
  const {title, subtitle} = usePrivacyText({
    ...workstream,
    metrics,
    employee_associations: owners,
    private_employees: privateEmployees?.map(({user_id}) => user_id),
    private_teams: privateTeams?.map(({team_id}) => team_id),
  });

  return (
    <Box display="flex" alignItems="center" style={{gap: 4}}>
      <UIIcon src={workstream.privacy === WorkstreamPrivacy.PUBLIC ? LockOpenLine : LockLine} />
      <Text color="subtle" isBold>
        {title}
      </Text>
      <Text color="subtle">{subtitle}</Text>
    </Box>
  );
};

const formatDate = (date: Date) =>
  date
    .toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    })
    .replace(',', '')
    .replace('AM', 'am')
    .replace('PM', 'pm');

const normalizeWorkstreamObjective = (description?: string | null) =>
  getPlainTextFromRichTextValue(description);

const normalizeWorkstreamObjectiveForEditor = (description?: string | null) => {
  if (!description) {
    return '';
  }

  return isLexicalJsonString(description) ? description : toPlainTextLexicalJson(description);
};

const serializeWorkstreamObjective = (description?: string | null) =>
  normalizeWorkstreamObjectiveForEditor(description);

const EmployeeSelect = ({
  isInvalid,
  selected,
  onSelect,
}: {
  isInvalid?: boolean;
  selected?: EmployeeLdap;
  onSelect: (employee?: Employee) => void;
}) => {
  // add default selected employees

  const selectEmployee = (employee: Employee) => {
    onSelect(employee);
  };

  const selectEmployees = (employees: Employee[]) => {
    if (employees.length > 0) {
      onSelect(employees[0]);
    }
  };

  const removeEmployee = () => {
    onSelect();
  };

  return (
    <PeopleSearchMenu
      typeaheadContext="workstream_people_scope"
      inputClassName={styles.employeeSelectInput}
      selectedEmployees={selected ? [selected as Employee] : []}
      onRemoveEmployee={removeEmployee}
      onSelectEmployee={selectEmployee}
      onSelectEmployees={selectEmployees}
      showReportingLine={true}
      isSingleSelect={true}
      searchIconPosition="left"
      allowSelf={true}
      isInvalid={isInvalid}
    />
  );
};

const WorkingGroupSelect = ({
  selected,
  onSelectEmployees,
  onRemoveEmployee,
  preferredEmployees,
}: {
  selected: EmployeeRoleLevel[];
  onSelectEmployees: (employees: Employee[]) => void;
  onRemoveEmployee: (employee: EmployeeRoleLevel) => void;
  preferredEmployees: Employee[];
}) => (
  <PeopleSearchMenu
    typeaheadContext="workstream_people_scope"
    inputClassName={styles.employeeSelectInput}
    selectedEmployees={selected as Employee[]}
    onRemoveEmployee={(employee) => onRemoveEmployee(employee as EmployeeRoleLevel)}
    onSelectEmployee={(employee) => onSelectEmployees([employee])}
    onSelectEmployees={onSelectEmployees}
    showReportingLine={false}
    searchIconPosition="left"
    allowSelf={true}
    preferredEmployees={preferredEmployees as Employee[]}
    sortEmployees={sortEmployees}
  />
);

export const WorkstreamModify = ({
  workstream,
  onSubmit,
  isEditing,
  isPending,
  disableModifyOutcomes,
}: {
  onSubmit: (data: {data: WorkstreamCreate | WorkstreamEdit; reason?: string}) => Promise<number>;
  workstream?: Workstream;
  isPending: boolean;
  isEditing?: boolean;
  disableModifyOutcomes?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {hasMnpiAccess} = useMnpiAccess();
  const {isWorkstreamTimeframesEnabled} = useAtomValue(growthbookCacheAtom);
  const timeframes = useTimeframes();
  // Editing only: a new workstream's timeframe isn't known until it's created.
  const timeframeRuleParent = isWorkstreamTimeframesEnabled ? workstream : undefined;

  const companyWorkstreams = useCompanyWorkstreams({timeframe: '2025'});
  const {hasPlanningDocs, isLoading: isPlanningDocsLoading} = useWorkstreamPlanningDocs(
    workstream?.id
  );
  const autofocus = location.state?.autofocus;
  const source = location.state?.source;
  const from = location.state?.from;
  const createType = location.state?.type;
  const sidebarOriginState = buildSidebarOriginState({state: location.state});

  useEffect(() => {
    if (autofocus === 'resources') {
      setDrawerOpen('settings');
      return;
    }

    if (autofocus === 'privacy') {
      setDrawerOpen('settings');
      setIsPrivacyDrawerOpen(true);
      setWorkstreamUpdate((prev) => ({
        ...prev,
        privacy: WorkstreamPrivacy.CUSTOM,
      }));
    }
  }, [autofocus]);

  const dueDateRaw =
    workstream?.status_due_date ?? companyWorkstreams?.trackers?.[0]?.status_due_date;
  const dueDate = dueDateRaw
    ? (() => {
        const [y, m, d] = dueDateRaw.slice(0, 10).split('-').map(Number);
        return format(new Date(y, m - 1, d), 'MMM d');
      })()
    : format(new Date(), 'MMM d');

  const [workstreamType, setWorkstreamType] = useState<'workstream' | 'tracker'>(
    createType ?? (workstream?.status_cadence === 'none' ? 'tracker' : 'workstream')
  );

  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const {employee: loggedInEmployee, teamMembers} = useAtomValue(loggedInEmployeeAtom);
  const [isDrawerOpen, setDrawerOpen] = useState<'activity' | 'settings' | undefined>(undefined);
  const [isPrivacyDrawerOpen, setIsPrivacyDrawerOpen] = useState(false);
  const [privateEmployees, setPrivateEmployees] = useState<EmployeeLdap[] | undefined>(
    workstream?.private_employees
  );
  const [privateTeams, setPrivateTeams] = useState<TeamWithCounts[] | undefined>(
    workstream?.private_teams
  );

  const [reason, setReason] = useState<string | undefined>();

  // Role assignments state
  const [roleAssignments, setRoleAssignments] = useState<RoleAssignments>(() =>
    initializeRoleAssignments(workstream)
  );
  const [workingGroupAssignments, setWorkingGroupAssignments] = useState<EmployeeRoleLevel[]>(() =>
    initializeWorkingGroupAssignments(workstream)
  );

  // Helper to get DRI from role assignments (for backward compatibility)
  const workstreamDri = roleAssignments.dri;

  const {data: auditLogs} = useAuditLogs(workstream?.id ?? 0, !isEditing);

  const hasDoneChanges = useMemo(() => auditLogs?.find((log) => log.type === 'done'), [auditLogs]);

  const teamSuggestions = useMemo(() => [...teamMembers].sort(sortEmployees), [teamMembers]);

  const employeeAssociations = useMemo(
    () => [
      ...Object.entries(roleAssignments)
        .filter(
          (entry): entry is [SingleWorkstreamRoleId, EmployeeRoleLevel] => entry[1] !== undefined
        )
        .map(([roleId, employee]) => ({
          dri: roleId,
          user_id: employee.user_id,
        })),
      ...workingGroupAssignments.map((employee) => ({
        dri: WORKING_GROUP_ROLE_ID,
        user_id: employee.user_id,
      })),
    ],
    [roleAssignments, workingGroupAssignments]
  );

  const handleRoleSelect = (roleId: SingleWorkstreamRoleId, employee?: Employee) => {
    setRoleAssignments((prev) => ({...prev, [roleId]: employee as EmployeeRoleLevel}));
  };

  const handleWorkingGroupSelect = (employees: Employee[]) => {
    setWorkingGroupAssignments((prev) => {
      const existingEmployeeIds = new Set(prev.map(({user_id}) => user_id));
      const nextEmployees = employees
        .filter((employee) => !existingEmployeeIds.has(employee.user_id))
        .map((employee) => employee as EmployeeRoleLevel);
      return [...prev, ...nextEmployees];
    });
  };

  const removeWorkingGroupEmployee = (employee: EmployeeRoleLevel) => {
    setWorkingGroupAssignments((prev) => prev.filter(({user_id}) => user_id !== employee.user_id));
  };

  const [endDate, setEndDate] = useState<string | null>(workstream?.end_date ?? null);
  const [dynamicEndDate, setDynamicEndDate] = useState<boolean>(!workstream?.end_date);

  // Filter out MNPI metrics the user doesn't have access to, but keep their IDs
  const filteredMetrics = useMemo(() => {
    if (!workstream?.metrics) return [];
    return (workstream.metrics ?? [])
      .filter((metric) => !isRedactedMetric(metric))
      .map((metric) => {
        // If metric is MNPI and user doesn't have access, keep a placeholder with just the ID
        if (metric.is_mnpi && !hasMnpiAccess && metric.title === 'RESTRICTED') {
          return {
            ...metric,
            _isRestricted: true, // Hidden marker for internal use
          } as WorkstreamMetric & {_isRestricted?: boolean};
        }
        return metric;
      })
      .sort(sortMetricsByCompleteDate);
  }, [workstream?.metrics, hasMnpiAccess]);

  const [metrics, setMetrics] = useState<WorkstreamMetric[]>(filteredMetrics);

  const [workstreams, setWorkstreams] = useState<WorkstreamSortWrapper[]>([
    ...(workstream?.dependencies ?? [])
      .sort((a, b) => a.order - b.order)
      .map((dependency, index) => ({
        id: index + 1,
        workstreamId: dependency.dependent?.id ?? -1,
        data: dependency.dependent ?? undefined,
      })),
  ]);

  // A parent without a timeframe joins its first new sub-workstream's on save.
  const pendingParentTimeframe = useMemo(() => {
    if (!timeframeRuleParent) {
      return undefined;
    }
    const savedIds = new Set(
      (workstream?.dependencies ?? []).map((dependency) => dependency.dependent?.id)
    );
    return getPendingParentTimeframe(
      timeframeRuleParent,
      workstreams
        .filter(({workstreamId}) => !savedIds.has(workstreamId))
        .map(({data}) => data ?? {})
    );
  }, [timeframeRuleParent, workstream?.dependencies, workstreams]);

  useDocumentTitle(isEditing ? t('edit_workstream') : t('add_workstream_or_tracker'));

  useEffect(() => {
    if (!workstream) {
      return;
    }

    analyticsLogger().logEvent('WORKSTREAM_MODIFY_VIEW', {
      edit: Boolean(isEditing),
      autofocus,
      source,
      owner: getUserType(workstream, loggedInEmployee),
    });
  }, [isEditing, autofocus, workstream, loggedInEmployee, source]);

  useEffect(() => {
    if (loggedInEmployee?.ldap && !roleAssignments.dri) {
      setRoleAssignments((prev) => ({...prev, dri: loggedInEmployee}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInEmployee?.ldap]);

  useEffect(() => {
    if (workstream) {
      setRoleAssignments(initializeRoleAssignments(workstream));
      setWorkingGroupAssignments(initializeWorkingGroupAssignments(workstream));
      setMetrics(filteredMetrics);
    }
  }, [workstream, filteredMetrics]);

  const [workstreamUpdate, setWorkstreamUpdate] = useState<WorkstreamEdit>(
    workstream
      ? {
          id: workstream.id,
          name: workstream.name,
          links: workstream.links,
          description: normalizeWorkstreamObjectiveForEditor(workstream.description),
          coaching_rules:
            (workstream as Workstream & {coaching_rules?: string | null}).coaching_rules ?? null,
          dependencies: metrics
            .map((dependency) => ({
              ...dependency,
              dependent_id: dependency.id,
            }))
            .filter((dependency) => !isWorkstreamMetric(dependency)) as WorkstreamDependencyEdit[],
          start_date: workstream.start_date,
          end_date: endDate,
          status_cadence: workstream.status_cadence,
          status_due: workstream.status_due,
          employee_associations: workstream.employee_associations.map((assoc) => ({
            user_id: assoc.employee.user_id,
            dri: assoc.dri,
          })),
          privacy: autofocus === 'privacy' ? WorkstreamPrivacy.CUSTOM : workstream.privacy,
        }
      : ({
          status_cadence: 'semimonthly',
          status_due: 1,
          end_date: null,
          description: '',
          coaching_rules: null,
          privacy: autofocus === 'privacy' ? WorkstreamPrivacy.CUSTOM : WorkstreamPrivacy.PUBLIC,
        } as unknown as WorkstreamEdit)
  );
  const objectivePlainText = useMemo(
    () => normalizeWorkstreamObjective(workstreamUpdate.description),
    [workstreamUpdate.description]
  );

  const {resetDirtyState} = useConfirmUnsavedChanges([
    workstreamUpdate.name,
    workstreamUpdate.description,
    workstreamUpdate.coaching_rules,
    workstreamUpdate.links,
    workstreamUpdate.privacy,
    JSON.stringify(roleAssignments),
    JSON.stringify(workingGroupAssignments),
    workstreamType,
    endDate,
    metrics,
    workstreams,
    privateEmployees,
    privateTeams,
  ]);

  const changedRequiredField = useMemo(() => {
    if (!workstream) {
      return false;
    }

    const existingMetrics = workstream.metrics.filter(
      (metric) =>
        !isRedactedMetric(metric) &&
        !(metric.is_mnpi && !hasMnpiAccess && metric.title === 'RESTRICTED')
    );
    const currentMetrics = metrics
      .filter(isWorkstreamMetric)
      .filter(({title}) => title)
      .filter((m) => !(m as WorkstreamMetric & {_isRestricted?: boolean})._isRestricted);

    if (currentMetrics.length < existingMetrics.length) {
      return true;
    }

    for (const existingMetric of existingMetrics) {
      const currentMetric = currentMetrics.find(
        (m) => (m.id ?? m.title) === (existingMetric.id ?? existingMetric.title)
      );
      if (!currentMetric) {
        return true;
      }
      if (currentMetric.target !== existingMetric.target) {
        return true;
      }
      if (
        currentMetric.complete_date !== getSoonestMetricDate(existingMetric) &&
        getSoonestMetricDate(existingMetric)
      ) {
        return true;
      }
    }

    // If we get here, either no changes were made or only new metrics were added
    return false;
  }, [workstream, metrics, hasMnpiAccess]);

  const hasInvalidLinks = useMemo(() => {
    const links = workstreamUpdate?.links;

    // Validate links
    let invalidLink = false;

    links?.forEach(({url}) => {
      if (!url) {
        return;
      }
      if (!isLinkValid(url, 'text')) {
        invalidLink = true;
        return;
      }
    });

    if (invalidLink || links?.some(({url, text}) => (url && !text) || (!url && text))) {
      return true;
    }

    return false;
  }, [workstreamUpdate?.links]);

  const hasError = useMemo(() => {
    const {name} = workstreamUpdate;

    if (!name) {
      return true;
    }

    // Check that all required roles are assigned
    const missingRequiredRole = getRequiredRoles().some(
      (role) => !roleAssignments[role.id as SingleWorkstreamRoleId]
    );
    if (missingRequiredRole) {
      return true;
    }

    // Validate metrics (exclude restricted MNPI metrics)
    const invalidMetric = metrics
      ?.filter(isWorkstreamMetric)
      ?.filter((m) => !(m as WorkstreamMetric & {_isRestricted?: boolean})._isRestricted)
      .some(
        ({type, title, current, target}) => type === 'metric' && title && (!current || !target)
      );
    if (invalidMetric) {
      return true;
    }

    // Validate links
    if (hasInvalidLinks) {
      return true;
    }

    if (!reason && changedRequiredField) {
      return true;
    }

    return false;
  }, [metrics, workstreamUpdate, roleAssignments, reason, changedRequiredField, hasInvalidLinks]);

  useEffect(() => {
    if (workstreamType === 'tracker') {
      setMetrics((prev) => prev.filter((metric) => (metric as WorkstreamMetric).title));
    }
  }, [workstreamType]);

  const handleSubmit = async () => {
    // Reset dirty state immediately when user clicks save to prevent unsaved changes prompt
    resetDirtyState();

    try {
      const id = await onSubmit({
        reason,
        data: {
          id: workstreamUpdate.id,
          name: workstreamUpdate.name,
          description: serializeWorkstreamObjective(workstreamUpdate.description),
          coaching_rules: workstreamUpdate.coaching_rules ?? null,
          start_date: workstreamUpdate.start_date ?? new Date().toISOString(),
          end_date: endDate,
          status_cadence:
            workstreamType === 'workstream' ? workstreamUpdate.status_cadence : 'none',
          status_due: workstreamUpdate.status_due,
          links: workstreamUpdate.links?.filter(({url, text}) => url && text),
          dependencies: getUniqueSelectedWorkstreamIds(workstreams)
            .filter((workstreamId) => workstreamId !== workstreamUpdate.id)
            .map((workstreamId, index) => ({
              dependent_id: workstreamId,
              type: WorkstreamDependencyType.DEPENDS_ON,
              order: index + 1,
            })) as WorkstreamDependencyEdit[],
          employee_associations: employeeAssociations,
          privacy: workstreamUpdate.privacy,
          private_employees:
            workstreamUpdate.privacy === WorkstreamPrivacy.CUSTOM
              ? privateEmployees?.map(({user_id}) => user_id)
              : undefined,
          private_teams:
            workstreamUpdate.privacy === WorkstreamPrivacy.CUSTOM
              ? privateTeams?.map(({team_id}) => team_id)
              : undefined,
        },
      });

      // Compare metrics vs workstream.metrics to determine changes
      if (workstream?.metrics) {
        metrics.forEach((metric) => {
          const existingMetric = workstream.metrics.find((m) => m.id === metric.id);

          if (!existingMetric) {
            analyticsLogger().logEvent('WORKSTREAM_MODIFIED', {
              source: 'modify',
              property: 'outcomes',
              type: 'add',
            });
          } else if (JSON.stringify(existingMetric) !== JSON.stringify(metric)) {
            analyticsLogger().logEvent('WORKSTREAM_MODIFIED', {
              source: 'modify',
              property: 'outcomes',
              type: 'edit',
            });
          }
        });

        workstream.metrics.forEach((originalMetric) => {
          const stillExists = metrics.some((m) => m.id === originalMetric.id);
          if (!stillExists) {
            // Metric was deleted
            analyticsLogger().logEvent('WORKSTREAM_MODIFIED', {
              source: 'modify',
              property: 'outcomes',
              type: 'delete',
            });
          }
        });
      } else {
        metrics.forEach(() => {
          analyticsLogger().logEvent('WORKSTREAM_MODIFIED', {
            source: 'modify',
            property: 'outcomes',
            type: 'add',
          });
        });
      }

      setSnackbarMessage({text: t('saved')});

      navigate(
        from && !isWorkstreamCompanyIndexPath(from)
          ? from
          : ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', id.toString()),
        {
          state: {
            source: isEditing ? 'modify' : 'create',
            workstreamId: id,
            ...sidebarOriginState,
          },
        }
      );
    } catch (e: any) {
      setSnackbarMessage({text: e?.message || String(e)});
    }
  };

  const isDraftWorkstream = isPlanningDocsLoading ? true : hasPlanningDocs;
  if (workstream?.archived && !isDraftWorkstream) {
    navigate(ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', workstream.id.toString()), {
      state: {source: 'modify-archived'},
      replace: true,
    });
    setSnackbarMessage({text: t('unarchive_first')});

    return null;
  }

  if (!loggedInEmployee?.email) {
    return null;
  }

  return (
    <Layout.InlineDrawerContainer
      open={Boolean(isDrawerOpen)}
      preventEscapeClose={isPrivacyDrawerOpen}
      childSheet={
        isPrivacyDrawerOpen
          ? {
              // icon: LockLine,
              header: <Title size={18}>{t('workstream_privacy')}</Title>,
              onClose: () => setIsPrivacyDrawerOpen(false),
              body: (
                <>
                  <WorkstreamPrivacyDrawer
                    owners={[
                      ...employeeAssociations,
                      ...(metrics
                        .filter(isWorkstreamMetric)
                        .map(({owner}) => ({
                          dri: 'outcome_dri',
                          user_id: owner?.user_id,
                        }))
                        .filter(({user_id}) => Boolean(user_id)) as WorkstreamEmployeeEdit[]),
                    ]}
                    selectedEmployees={privateEmployees}
                    selectedTeams={privateTeams}
                    setSelectedEmployees={setPrivateEmployees}
                    setSelectedTeams={setPrivateTeams}
                  />
                </>
              ),
            }
          : undefined
      }
      drawerHeader={
        <Title size={18}>
          {isDrawerOpen === 'activity'
            ? t('workstream_activity')
            : isDrawerOpen === 'settings'
              ? t('edit_details')
              : null}
        </Title>
      }
      drawerIcon={undefined}
      drawerBody={
        isDrawerOpen === 'activity' ? (
          workstream && <WorkstreamAuditLog workstream={workstream} />
        ) : isDrawerOpen === 'settings' ? (
          <>
            <FormRow>
              <Cluster>
                <FormLabel subtext={<Toggletip title={t('project_resources_tooltip')} />}>
                  {t('resources')}
                </FormLabel>
                <Text color="faint" size="small" className={atoms({marginLeft: 'auto'})}>
                  {t('optional')}
                </Text>
              </Cluster>
              <ProjectLinkEditor
                stacked
                isInvalid={hasError && hasInvalidLinks}
                autoFocus={autofocus === 'resources'}
                links={
                  workstreamUpdate.links?.length
                    ? (workstreamUpdate.links as Link[])
                    : ([{url: '', text: ''}] as Link[])
                }
                updateLinks={(links) => setWorkstreamUpdate((p) => ({...p, links}))}
              />
            </FormRow>

            <FormRow>
              <Cluster>
                <FormLabel>{t('workstream_objective')}</FormLabel>
                <Text color="faint" size="small" className={atoms({marginLeft: 'auto'})}>
                  {t('optional')}
                </Text>
              </Cluster>
              <RichTextArea
                editable
                value={workstreamUpdate.description ?? ''}
                placeholder={t('add_workstream_objective')}
                hideToolbar
                links={false}
                mentions={false}
                disableTables
                disableBorder
                minHeight={96}
                editablePaddingX="0"
                editablePaddingY="0"
                contentClassName={styles.plainRichTextContent}
                onChange={(editorState) =>
                  setWorkstreamUpdate((p) => ({...p, description: editorState.toString()}))
                }
              />
            </FormRow>

            {workstreamType === 'workstream' && (
              <FormRow>
                <Cluster>
                  <FormLabel>{t('ai_coach_rules_label')}</FormLabel>
                  <Text color="faint" size="small" className={atoms({marginLeft: 'auto'})}>
                    {t('optional')}
                  </Text>
                </Cluster>
                {workstreams.some((w) => w.workstreamId > 0) && (
                  <FormHelperText>{t('ai_coach_rules_helper')}</FormHelperText>
                )}
                <TextArea
                  disabled={!workstreams.some((w) => w.workstreamId > 0)}
                  value={workstreamUpdate.coaching_rules ?? ''}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                    setWorkstreamUpdate((p) => ({
                      ...p,
                      coaching_rules: e.target.value || null,
                    }))
                  }
                  placeholder={
                    workstreams.some((w) => w.workstreamId > 0)
                      ? t('ai_coach_rules_placeholder')
                      : t('ai_coach_rules_helper_no_subworkstreams')
                  }
                  rows={4}
                  style={{fontSize: '90%', lineHeight: '1.5em'}}
                />
              </FormRow>
            )}

            {workstreamType === 'workstream' && (
              <WorkstreamSettingsDrawer
                workstream={workstreamUpdate}
                owners={[
                  ...employeeAssociations,
                  ...(metrics
                    .filter(isWorkstreamMetric)
                    .map(({owner}) => ({
                      dri: 'outcome_dri',
                      user_id: owner?.user_id,
                    }))
                    .filter(({user_id}) => user_id !== null) as WorkstreamEmployeeEdit[]),
                ]}
                privateEmployees={privateEmployees ?? []}
                privateTeams={privateTeams ?? []}
                // setStatusDue={(statusDue) =>
                //   setWorkstreamUpdate((p) => ({...p, status_due: statusDue}))
                // }
                onPrivacyChange={(privacy) => {
                  if (privacy === WorkstreamPrivacy.CUSTOM) {
                    setIsPrivacyDrawerOpen(true);
                  }
                  return setWorkstreamUpdate((p) => ({
                    ...p,
                    privacy,
                  }));
                }}
              />
            )}
          </>
        ) : null
      }
      onClose={() => setDrawerOpen(undefined)}
    >
      <Stack align="start">
        <Header isEditing={isEditing} />

        {!workstream?.id && source !== 'workstreams_v2' && (
          <FormRow>
            <Split gap="8" style={{alignItems: 'stretch'}}>
              <Split.Item width="fill">
                <Box
                  cursor="pointer"
                  borderRadius="Medium"
                  paddingX="20"
                  paddingY="16"
                  borderStyle="Solid"
                  height="100%"
                  borderColor="Border Subtle"
                  {...withShade({
                    state: workstreamType === 'workstream' ? 'active' : undefined,
                  })}
                  onClick={() => setWorkstreamType('workstream')}
                >
                  <LabelGroup
                    withText={<Text isBold>{t('workstream')}</Text>}
                    withSubtext={t('workstream_description_helper')}
                    withLeftAccessory={
                      <EndToEndWorkflowMini width={64} altText={t('workstream')} />
                    }
                  />
                </Box>
              </Split.Item>
              <Split.Item width="fill">
                <Box
                  cursor="pointer"
                  borderRadius="Medium"
                  paddingX="20"
                  paddingY="16"
                  borderStyle="Solid"
                  height="100%"
                  borderColor="Border Subtle"
                  {...withShade({
                    state: workstreamType === 'tracker' ? 'active' : undefined,
                  })}
                  onClick={() => setWorkstreamType('tracker')}
                >
                  <LabelGroup
                    withText={<Text isBold>{t('tracker')}</Text>}
                    withSubtext={t('tracker_description_helper')}
                    withLeftAccessory={<EndToEndStackMini width={64} altText={t('tracker')} />}
                  />
                </Box>
              </Split.Item>
            </Split>
          </FormRow>
        )}

        <FormRow>
          <FormLabel
            withInput={
              <>
                <TextInput
                  required
                  aria-label={t('workstream_edit_name')}
                  autoFocus={!autofocus}
                  size="large"
                  width="100%"
                  isInvalid={hasError && !workstreamUpdate.name}
                  placeholder={t('start_writing_placeholder')}
                  value={workstreamUpdate.name}
                  onChange={(e) => setWorkstreamUpdate((p) => ({...p, name: e.target.value}))}
                />

                {/* <Text monospace className={atoms({margin: '16', paddingY: '8'})}>
                    https://os.dropbox.com/{teamUpdate.name.toLowerCase().replace(' ', '-')}
                  </Text> */}
              </>
            }
          >
            {t('workstream_edit_name')}
          </FormLabel>
        </FormRow>

        {workstreamType === 'workstream' && (
          <FormRow>
            <Stack gap="4">
              {WORKSTREAM_ROLE_ROWS.map((roleRow) => (
                <Split key={roleRow.join('-')} gap="16">
                  {roleRow.map((roleId) => {
                    const role = WORKSTREAM_ROLES[roleId];
                    return (
                      <Split.Item key={role.id} width="fill">
                        <Stack gap="4">
                          <Split>
                            <Split.Item width="fill">
                              <FormLabel>{role.label}</FormLabel>
                            </Split.Item>
                            {!role.required && (
                              <Split.Item>
                                <Text color="faint" size="small">
                                  {t('optional')}
                                </Text>
                              </Split.Item>
                            )}
                          </Split>
                          <EmployeeSelect
                            isInvalid={hasError && role.required && !roleAssignments[role.id]}
                            selected={roleAssignments[role.id]}
                            onSelect={(employee) => handleRoleSelect(role.id, employee)}
                          />
                        </Stack>
                      </Split.Item>
                    );
                  })}
                </Split>
              ))}

              <Stack gap="4">
                <Split>
                  <Split.Item width="fill">
                    <FormLabel>{WORKSTREAM_ROLES[WORKING_GROUP_ROLE_ID].label}</FormLabel>
                  </Split.Item>
                  <Split.Item>
                    <Text color="faint" size="small">
                      {t('optional')}
                    </Text>
                  </Split.Item>
                </Split>
                <WorkingGroupSelect
                  selected={workingGroupAssignments}
                  onSelectEmployees={handleWorkingGroupSelect}
                  onRemoveEmployee={removeWorkingGroupEmployee}
                  preferredEmployees={teamSuggestions}
                />
              </Stack>
            </Stack>
          </FormRow>
        )}

        {workstreamType === 'workstream' && (
          <Box style={{marginTop: 24}}>
            <FormRow>
              <FormLabel>{t('workstream_edit_end_date')}</FormLabel>
              <DatePickerInput
                id="choose-date"
                autocomplete="off"
                autoFocus={autofocus === 'eta'}
                placeholder={t('pick_a_date')}
                value={endDate ? toCalendarDate(endDate) : null}
                onChange={(date) => {
                  setEndDate(toEODISOStringFromDate(date));
                  setDynamicEndDate(false);
                }}
              />
            </FormRow>
          </Box>
        )}

        {workstreamType === 'workstream' && !disableModifyOutcomes && (
          <FormRow>
            <FormLabel>{t('workstream_edit_done')}</FormLabel>

            <MetricEditor
              workstreamId={workstreamUpdate.id}
              isInvalid={hasError}
              dri={workstreamDri}
              metrics={metrics}
              updateMetrics={(newMetrics) => {
                setMetrics(newMetrics);

                if (dynamicEndDate) {
                  const latestCompleteDate = newMetrics
                    .filter(isWorkstreamMetric)
                    .reduce((latest, metric) => {
                      const metricDate = getSoonestMetricDate(metric);
                      if (metricDate && new Date(metricDate) > latest) {
                        return new Date(metricDate);
                      }
                      return latest;
                    }, new Date(0));

                  if (latestCompleteDate.getTime() > 0) {
                    setEndDate(latestCompleteDate.toISOString());
                  } else {
                    setEndDate(null);
                  }
                }
              }}
              autofocus={autofocus === 'outcomes'}
            />

            {hasDoneChanges && (
              <FormHelperText>
                <Split alignY="center" gap="8">
                  <Split.Item>
                    <Text size="small" color="subtle">
                      {t('workstream_edit_done_changes', {
                        date_string: formatDate(new Date(hasDoneChanges.date + 'Z')),
                      })}
                    </Text>
                  </Split.Item>
                  <Split.Item>
                    <Button
                      variant="transparent"
                      onClick={() => setDrawerOpen('activity')}
                      size="small"
                    >
                      {t('view_activity')}
                    </Button>
                  </Split.Item>
                </Split>
              </FormHelperText>
            )}
          </FormRow>
        )}

        <FormRow>
          <FormLabel>
            {workstreamType === 'workstream' ? t('sub_workstreams') : t('workstreams')}
          </FormLabel>

          <ReordableWorkstreams
            autoFocus={autofocus === 'workstreams'}
            setAutoFocus={() => {}}
            workstreams={workstreams}
            setWorkstreams={setWorkstreams}
            blockedWorkstreamIds={workstreamUpdate.id ? [workstreamUpdate.id] : undefined}
            getDisabledReason={
              timeframeRuleParent
                ? (candidate) =>
                    getSubworkstreamTimeframeBlock(
                      timeframeRuleParent,
                      candidate,
                      timeframes,
                      pendingParentTimeframe
                    )
                : undefined
            }
          />
          {timeframeRuleParent && (
            <FormHelperText>
              {getSubworkstreamTimeframeRule(timeframeRuleParent, pendingParentTimeframe)}
            </FormHelperText>
          )}
        </FormRow>

        <FormRow>
          <FormLabel>
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              {t('details')}

              <Button onClick={() => setDrawerOpen('settings')} variant="transparent" size="small">
                {t('edit_details')}
              </Button>
            </Box>
          </FormLabel>

          <Stack
            gap="8"
            className={atoms({
              color: 'Text Subtle',
              borderLeft: 'Solid',
              borderColor: 'Border Subtle',
              paddingX: '12',
              paddingY: '8',
            })}
          >
            <Box display="flex" alignItems="center" style={{gap: 4}}>
              <UIIcon src={InfoLine} />
              <Text color="subtle" isBold>
                {t('workstream_objective')}
              </Text>
              {objectivePlainText ? (
                <Box style={{maxWidth: 300, overflow: 'hidden'}}>
                  <Text color="subtle" style={{whiteSpace: 'nowrap', textOverflow: 'ellipsis'}}>
                    {objectivePlainText}
                  </Text>
                </Box>
              ) : (
                <Text color="subtle">
                  <Button
                    size="small"
                    variant="transparent"
                    onClick={() => setDrawerOpen('settings')}
                  >
                    <Text color="subtle">{t('add_workstream_objective')}</Text>
                  </Button>
                </Text>
              )}
            </Box>

            <Box
              display="flex"
              alignItems="center"
              style={{gap: 4, marginLeft: -4}}
              width="100%"
              borderRadius="Medium"
              backgroundColor={hasError && hasInvalidLinks ? 'Alert On Base' : undefined}
              padding="4"
            >
              <UIIcon src={LinkLine} />
              <Text isBold color="subtle">
                {t('workstream_audit_link')}
              </Text>
              <Text color="subtle">
                {hasError && hasInvalidLinks ? (
                  t('workstream_resources_invalid')
                ) : workstreamUpdate.links?.length ? (
                  t('workstream_resources', {count: workstreamUpdate.links?.length ?? 0})
                ) : (
                  <Button
                    size="small"
                    variant="transparent"
                    onClick={() => setDrawerOpen('settings')}
                  >
                    <Text color="subtle">{t('add_resources')}</Text>
                  </Button>
                )}
              </Text>
            </Box>

            {workstreamType === 'workstream' && (
              <Suspense fallback={<Skeleton.Box height={24} width={250} />}>
                <PrivacyInfo
                  workstream={workstreamUpdate}
                  metrics={metrics.filter(isWorkstreamMetric)}
                  privateEmployees={privateEmployees}
                  privateTeams={privateTeams}
                  owners={employeeAssociations}
                />
              </Suspense>
            )}
          </Stack>
        </FormRow>

        {changedRequiredField && (
          <FormRow>
            <Box
              backgroundColor="Background Subtle"
              padding="16"
              borderRadius="Medium"
              marginTop="16"
            >
              <Cluster className={atoms({marginBottom: '8'})}>
                <Text>
                  <Trans
                    i18nKey="workstream_edit_update_reason"
                    t={t}
                    components={{b: <Text isBold />}}
                  />
                </Text>
              </Cluster>
              <TextArea
                isInvalid={!reason}
                placeholder={t('start_writing_placeholder')}
                size="large"
                value={reason}
                resizable="auto"
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
              />
            </Box>
          </FormRow>
        )}

        <Box
          position="fixed"
          bottom="0"
          backgroundColor="Background Base"
          width="100%"
          style={{maxWidth: 1620, left: 57}}
          borderTop="Solid"
          borderColor="Border Subtle"
        >
          <Box width="100%" style={{maxWidth: 1024, margin: '0 auto'}} paddingY="16">
            <EditSaveButtons
              border={false}
              cta={t('save')}
              disableSave={hasError}
              isLoading={isPending}
              handleCancelClick={() => {
                /* eslint-disable-next-line*/
                /* @ts-ignore */
                navigate(-1, {
                  state: {source: isEditing ? 'modify' : 'create'},
                });
              }}
              handleSaveClick={handleSubmit}
              containerStyleProps={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingLeft: '0',
              }}
              extraContent={
                workstreamType === 'workstream' ? (
                  <Box display="flex" alignItems="center" style={{gap: 4}}>
                    <UIIcon src={InfoLine} className={atoms({color: 'Text Subtle'})} />
                    <Text color="subtle">{t('workstream_edit_updates_due', {date: dueDate})}</Text>
                  </Box>
                ) : (
                  <Box />
                )
              }
            />
          </Box>
        </Box>
      </Stack>
    </Layout.InlineDrawerContainer>
  );
};

const Header = ({isEditing}: {isEditing?: boolean}) => {
  return (
    <>
      <Split alignY="center" paddingBottom="16" gap="6">
        <Split.Item width="fill">
          <Title size={18}>{isEditing ? 'Edit' : 'Create'}</Title>
        </Split.Item>
      </Split>
    </>
  );
};
